import { graphql } from 'react-relay';
import { JiraIssueTypeAri } from '@atlassian/ari/jira/issue-type';
import type { issueTypeQuery as issueTypeQueryType } from '@atlassian/jira-relay/src/__generated__/issueTypeQuery.graphql';
import { FieldValueLookupError } from '../../../common/errors';
import type { IssueTypePublicSetValue } from '../../../common/fields/issue-type';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type {
	FieldOptionsFilter,
	OptionsVisibility,
} from '../../../common/types/options-visibility';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { validateSetOptionsVisibilityRule } from '../../../common/utils/get-common-field-validators';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { optionsVisibilityContainNoMoreThan100Items } from '../../../common/utils/options-visibility';

export type IssueTypeInternalValue = {
	avatar: {
		small: string;
	};
	label: string;
	value: string;
};

export type IssueTypePublicGetValue = {
	id: string;
	name: string;
} | null;

export const issueTypeGetValueTransformer = (
	value: IssueTypeInternalValue | null,
): IssueTypePublicGetValue => {
	if (!value) {
		return null;
	}

	return {
		id: getResourceIdfromAri(value.value),
		name: value.label,
	};
};

// eslint-disable-next-line jira/relay/must-use-inline
export const issueTypeQuery = graphql`
	query issueTypeQuery($ari: ID!, $filterById: JiraFieldOptionIdsFilterInput) {
		node(id: $ari) {
			... on JiraIssueTypeField {
				# eslint-disable-next-line relay/unused-fields
				issueTypesForTransition(filterById: $filterById) @optIn(to: "JiraIssueTypesForTransition") {
					edges {
						node {
							value: id
							label: name
							avatar {
								small
							}
						}
					}
				}
			}
		}
	}
`;

export const issueTypeSetValueTransformer = async (
	inputValue: IssueTypePublicSetValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<IssueTypeInternalValue> => {
	const issueTypeARI = JiraIssueTypeAri.create({
		siteId: tenantContext.cloudId,
		issueTypeId: inputValue,
	}).toString();

	// it depends on jira-issue-type-field-options-filtering FF (add as dependency)
	const response: {
		edges?: [
			{
				node?: {
					value: string;
					label?: string | null;
					avatar?: {
						small?: string | null;
					};
				};
			},
		];
	} = await fetchFieldOptions<issueTypeQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs: [issueTypeARI],
		nodePropertyName: 'issueTypesForTransition',
		queryName: 'FETCH_ISSUE_TYPE',
		query: issueTypeQuery,
	});

	const issueType = response?.edges?.[0]?.node;

	if (issueType?.value !== issueTypeARI) {
		throw new FieldValueLookupError(inputValue);
	}

	return {
		value: issueType.value,
		label: issueType.label ?? '',
		avatar: {
			small: issueType?.avatar?.small ?? '',
		},
	};
};

export const validateSetOptionsVisibilityIssueType = (
	optionsVisibility: OptionsVisibility,
): boolean =>
	validateSetOptionsVisibilityRule(optionsVisibility) &&
	optionsVisibilityContainNoMoreThan100Items(optionsVisibility);

export const transformIssueTypeSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	_lookupValues: undefined,
	{ tenantContext }: PublicToInternalPropertyTransformerContext,
): FieldOptionsFilter => ({
	optionIds: optionsVisibility.options.map((optionId) =>
		JiraIssueTypeAri.create({
			siteId: tenantContext.cloudId,
			issueTypeId: optionId,
		}).toString(),
	),
	operation: optionsVisibility.isVisible ? 'ALLOW' : 'EXCLUDE',
});
